import { useState, useEffect } from 'react';
import { formatDateMedium } from '../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import EmptyState from '../../../../components/EmptyState';
import { StatusBadge } from '../../../../components/DataTable';

interface ModalContext {
  type: 'customer' | 'property' | 'room';
  id: string;
  name: string;
  propertyId?: string;
  propertyName?: string;
  properties?: { id: string }[];
}

interface ServicesModalProps {
  context: ModalContext;
  onClose: () => void;
  supabase: SupabaseClient;
}

interface ServiceCert {
  id: string;
  service_date: string | null;
  asset_ref: string | null;
  customer_name: string | null;
  operator_name: string | null;
  submission_status: string;
  primary_asset_description: string | null;
  property_id: string | null;
  room_id: string | null;
}

export default function ServicesModal({ context, onClose, supabase }: ServicesModalProps) {
  const [services, setServices] = useState<ServiceCert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadServices();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [context]);

  const loadServices = async () => {
    try {
      setLoading(true);

      if (context.type === 'customer') {
        const propertyIds = context.properties?.map(p => p.id) || [];
        if (propertyIds.length === 0) {
          setServices([]);
          setLoading(false);
          return;
        }
        const { data, error } = await supabase
          .from('cert_submissions')
          .select('*')
          .in('property_id', propertyIds)
          .eq('submission_status', 'certified')
          .order('service_date', { ascending: false })
          .limit(50);
        if (error) throw error;
        setServices((data as ServiceCert[]) || []);
        return;
      }

      if (context.type === 'room') {
        // Room-level services live in hosp_service_history (one row per
        // linked invoice × room). cert_submissions.room_id is rarely set
        // for POS batch services, so we query the fan-out table instead
        // and enrich with invoice_number for display.
        const { data, error } = await supabase
          .from('hosp_service_history')
          .select('id, service_date, service_type, invoice_id, invoices(invoice_number)')
          .eq('room_id', context.id)
          .order('service_date', { ascending: false })
          .limit(50);
        if (error) throw error;
        const rows: ServiceCert[] = (data || []).map((r: Record<string, unknown>) => ({
          id: r.id as string,
          service_date: r.service_date as string | null,
          asset_ref: (r.invoices as { invoice_number?: string } | null)?.invoice_number || null,
          customer_name: null,
          operator_name: null,
          submission_status: 'certified',
          primary_asset_description: `Serviced as part of linked invoice ${(r.invoices as { invoice_number?: string } | null)?.invoice_number || ''}`.trim(),
          property_id: context.propertyId || null,
          room_id: context.id,
        }));
        setServices(rows);
        return;
      }

      // property branch
      const { data, error } = await supabase
        .from('cert_submissions')
        .select('*')
        .eq('property_id', context.id)
        .eq('submission_status', 'certified')
        .order('service_date', { ascending: false })
        .limit(50);

      if (error) throw error;
      setServices((data as ServiceCert[]) || []);
    } catch (err: unknown) {
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  };

  const getContextTitle = () => {
    if (context.type === 'customer') return `Service History for ${context.name}`;
    if (context.type === 'property') return `Service History for ${context.name}`;
    if (context.type === 'room') return `Service History for ${context.name}`;
    return 'Service History';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2 className="modal-title">{getContextTitle()}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '60vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <LoadingSpinner />
            </div>
          ) : services.length === 0 ? (
            <EmptyState message="No service history found." />
          ) : (
            <div className="flex flex-col gap-md">
              {services.map((service) => (
                  <div
                    key={service.id}
                    style={{
                      padding: '0.75rem 1rem',
                      background: 'var(--color-bg-secondary)',
                      borderRadius: 'var(--radius-md)'
                    }}
                  >
                    <div className="flex justify-between items-center mb-xs">
                      <div className="flex items-center gap-sm">
                        <span className="fw-500">
                          {service.asset_ref || `#${service.id.slice(0, 8)}`}
                        </span>
                        <StatusBadge bg="var(--color-success-bg)" color="var(--color-success)">Certified</StatusBadge>
                      </div>
                    </div>
                    <div className="text-xs text-muted">
                      {service.service_date && formatDateMedium(service.service_date)}
                      {service.operator_name && <span> • {service.operator_name}</span>}
                      {service.customer_name && <span> • {service.customer_name}</span>}
                    </div>
                    {service.primary_asset_description && (
                      <div className="text-xs text-muted mt-xs">
                        {service.primary_asset_description}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
